import type { Rgba } from '../color.ts';
import type { SettingsVersion } from './version.ts';

export const RECENT_COLOR_LIMIT = 20;
export const RECENT_MAP_LIMIT = 5;
export const DEFAULT_REFERENCE_IMAGE_ALPHA = 0.2;

/** Catalog map the editor opened or saved, newest first. `id` is the `maps/<id>.json` stem. */
export type RecentMap = {
  id: string;
  label: string;
};

/** Chrome the editor remembers across visits. Add fields here; migrate defaults to the old hard-coded value. */
export type EditorSettings = {
  logVisible: boolean;
  logFilter: 'both' | 'errors' | 'warnings';
  showScale: boolean;
  /** True-size hull parked beside the cursor. Off until View → Boat is turned on. */
  showBoat: boolean;
  showMinimap: boolean;
  /** Routes menu toggle. Authored routes stay on the map when this is off. */
  showRoutes: boolean;
  snapToGrid: boolean;
  previewLighting: boolean;
  /** Play-mode 2.5-D island/rock columns. Off while authoring; yesterday this followed Preview lighting. */
  showElevation: boolean;
  /** Viewport grid stroke. Off until View → Grid is turned on; colour/alpha stay on the map. */
  showGrid: boolean;
  /** View → Perf readout (docs/PERFORMANCE.md). Diagnostic, so off unless asked for. */
  showPerf: boolean;
  /** Last editor tracing-overlay opacity. The bitmap itself is session-only. */
  referenceImageAlpha: number;
  /** Draw terraces strip — last Levels / Peak / Radius / colour variation / randomness. Not a map field. */
  terraceLevels: number;
  terracePeakM: number;
  terraceRadius: number;
  terraceColorVariation: number;
  terraceJitter: number;
  terraceElevRand: number;
  terraceRadiusRand: number;
  recentMaps: RecentMap[];
};

/**
 * Player render preferences (v3). `smoothEdges` is canvas MSAA (`antialiasGL`): it cost
 * 6–12 ms of GPU per frame at 3440×1440 on an integrated GPU (docs/PERFORMANCE.md), so a
 * player can trade it for a steadier frame rate. Read once at boot; changing it reloads.
 */
export type RenderSettings = {
  smoothEdges: boolean;
};

export type SettingsDocument = {
  version: SettingsVersion;
  recentColors: Rgba[];
  editor: EditorSettings;
  render: RenderSettings;
};
